import { auth } from '@/lib/firebase'
import { BOARD_POST_DRAFTS_ENDPOINT } from '@/config/endpoints'
import type { CommunityAudience, CommunityPostType } from '@/lib/communityPostData'

export type BoardPostDraftStatus = 'pending' | 'published' | 'discarded'

export type BoardPostDraftDetectedType =
  | 'new_tattoo'
  | 'event_announcement'
  | 'discount'
  | 'flash_drop'
  | 'general'

export type BoardPostDraftSuggestedPostType = 'new_tattoo' | 'flash_drop' | 'deal' | 'general'

export interface BoardPostDraft {
  readonly id: string
  readonly sourcePostId: string
  readonly sourcePostUrl: string | null
  readonly sourcePostCaption: string
  readonly sourceInstagramHandle: string
  readonly artistId: string | null
  readonly artistCity: string | null
  readonly artistCountry: string | null
  readonly detectedType: BoardPostDraftDetectedType
  readonly suggestedPostType: BoardPostDraftSuggestedPostType
  readonly suggestedTitle: string
  readonly suggestedBody: string | null
  readonly imageUrls: string[]
  readonly imageStoragePaths?: string[]
  readonly primaryPortfolioImageIndex: number
  readonly status: BoardPostDraftStatus
  readonly classificationReason: string
  readonly sourcePostTimestamp: string | null
  readonly scrapedAt: string | null
  readonly createdAt: string | null
  readonly publishedPostId: string | null
}

export type BoardPostDraftDetectedTypeFilter = BoardPostDraftDetectedType | 'all'

const VALID_DETECTED_TYPES: readonly BoardPostDraftDetectedType[] = [
  'new_tattoo',
  'event_announcement',
  'discount',
  'flash_drop',
  'general',
]

export function resolveDraftDetectedType(draft: BoardPostDraft): BoardPostDraftDetectedType {
  if (VALID_DETECTED_TYPES.includes(draft.detectedType)) {
    return draft.detectedType
  }
  if (draft.suggestedPostType === 'deal') {
    return 'discount'
  }
  if (draft.suggestedPostType === 'flash_drop') {
    return 'flash_drop'
  }
  if (draft.suggestedPostType === 'new_tattoo') {
    return 'new_tattoo'
  }
  return 'general'
}

export function filterBoardPostDraftsLocally(
  drafts: readonly BoardPostDraft[],
  filters: {
    readonly detectedType?: BoardPostDraftDetectedTypeFilter
    readonly maxPostAgeDays?: BoardPostDraftMaxPostAgeDays
  },
): BoardPostDraft[] {
  const nowMs = Date.now()

  return drafts.filter((draft) => {
    if (filters.detectedType && filters.detectedType !== 'all') {
      if (resolveDraftDetectedType(draft) !== filters.detectedType) {
        return false
      }
    }

    if (filters.maxPostAgeDays != null) {
      if (!draft.sourcePostTimestamp) {
        return false
      }
      const postTimeMs = new Date(draft.sourcePostTimestamp).getTime()
      if (Number.isNaN(postTimeMs)) {
        return false
      }
      const maxAgeMs = filters.maxPostAgeDays * 24 * 60 * 60 * 1000
      if (nowMs - postTimeMs > maxAgeMs) {
        return false
      }
    }

    return true
  })
}

export type BoardPostDraftMaxPostAgeDays = 7 | 14 | 30 | null

interface BoardPostDraftsListResponse {
  readonly drafts: BoardPostDraft[]
}

async function buildAdminRequestHeaders(): Promise<HeadersInit> {
  const currentUser = auth.currentUser
  if (!currentUser) {
    throw new Error('Authentication required')
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await currentUser.getIdToken()}`,
  }
}

async function parseErrorResponse(response: Response): Promise<string> {
  const errorBody = await response.json().catch(() => ({}))
  return typeof errorBody.detail === 'string' ? errorBody.detail : response.statusText
}

export async function fetchBoardPostDrafts(params: {
  readonly status?: BoardPostDraftStatus
  readonly detectedType?: BoardPostDraftDetectedTypeFilter
  readonly maxPostAgeDays?: BoardPostDraftMaxPostAgeDays
  readonly limit?: number
}): Promise<BoardPostDraft[]> {
  const searchParams = new URLSearchParams()
  if (params.status) {
    searchParams.set('status', params.status)
  }
  if (params.limit) {
    searchParams.set('limit', String(params.limit))
  }

  const queryString = searchParams.toString()
  const url = queryString
    ? `${BOARD_POST_DRAFTS_ENDPOINT}?${queryString}`
    : BOARD_POST_DRAFTS_ENDPOINT

  const response = await fetch(url, {
    method: 'GET',
    headers: await buildAdminRequestHeaders(),
  })

  if (!response.ok) {
    throw new Error(await parseErrorResponse(response))
  }

  const payload = (await response.json()) as BoardPostDraftsListResponse
  const drafts = payload.drafts ?? []

  return filterBoardPostDraftsLocally(drafts, {
    detectedType: params.detectedType,
    maxPostAgeDays: params.maxPostAgeDays,
  })
}

export async function updateBoardPostDraft(
  draftId: string,
  patch: {
    readonly suggestedTitle?: string
    readonly suggestedBody?: string | null
    readonly imageUrls?: readonly string[]
    readonly     suggestedPostType?: BoardPostDraftSuggestedPostType
    primaryPortfolioImageIndex?: number
    readonly artistCity?: string | null
    readonly artistCountry?: string | null
  },
): Promise<BoardPostDraft> {
  const response = await fetch(`${BOARD_POST_DRAFTS_ENDPOINT}/${encodeURIComponent(draftId)}`, {
    method: 'PATCH',
    headers: await buildAdminRequestHeaders(),
    body: JSON.stringify({
      suggestedTitle: patch.suggestedTitle,
      suggestedBody: patch.suggestedBody,
      imageUrls: patch.imageUrls ? [...patch.imageUrls] : undefined,
      suggestedPostType: patch.suggestedPostType,
      primaryPortfolioImageIndex: patch.primaryPortfolioImageIndex,
      artistCity: patch.artistCity,
      artistCountry: patch.artistCountry,
    }),
  })

  if (!response.ok) {
    throw new Error(await parseErrorResponse(response))
  }

  return response.json() as Promise<BoardPostDraft>
}

export async function publishBoardPostDraft(
  draftId: string,
  options: {
    readonly city?: string
    readonly country?: string | null
    readonly     audience?: CommunityAudience
    readonly authorDisplayName?: string | null
    readonly primaryPortfolioImageIndex?: number
  },
): Promise<{ draft: BoardPostDraft; postId: string }> {
  const response = await fetch(
    `${BOARD_POST_DRAFTS_ENDPOINT}/${encodeURIComponent(draftId)}/publish`,
    {
      method: 'POST',
      headers: await buildAdminRequestHeaders(),
      body: JSON.stringify({
        city: options.city,
        country: options.country ?? null,
        audience: options.audience ?? 'everyone',
        authorDisplayName: options.authorDisplayName ?? null,
        primaryPortfolioImageIndex: options.primaryPortfolioImageIndex,
      }),
    },
  )

  if (!response.ok) {
    throw new Error(await parseErrorResponse(response))
  }

  const payload = (await response.json()) as {
    draft: BoardPostDraft
    postId: string
  }
  return { draft: payload.draft, postId: payload.postId }
}

export async function discardBoardPostDraft(draftId: string): Promise<BoardPostDraft> {
  const response = await fetch(`${BOARD_POST_DRAFTS_ENDPOINT}/${encodeURIComponent(draftId)}`, {
    method: 'DELETE',
    headers: await buildAdminRequestHeaders(),
  })

  if (!response.ok) {
    throw new Error(await parseErrorResponse(response))
  }

  return response.json() as Promise<BoardPostDraft>
}

export function boardDraftPostTypeLabel(postType: BoardPostDraftSuggestedPostType): string {
  const labels: Record<BoardPostDraftSuggestedPostType, string> = {
    new_tattoo: 'New tattoo',
    flash_drop: 'Flash drop',
    deal: 'Deal',
    general: 'General',
  }
  return labels[postType]
}

export function boardDraftDetectedTypeLabel(detectedType: BoardPostDraftDetectedType): string {
  const labels: Record<BoardPostDraftDetectedType, string> = {
    new_tattoo: 'New tattoo',
    event_announcement: 'Event',
    discount: 'Discount',
    flash_drop: 'Flash drop',
    general: 'General',
  }
  return labels[detectedType]
}

export function mapDraftSuggestedTypeToCommunityPostType(
  suggestedType: BoardPostDraftSuggestedPostType,
): CommunityPostType {
  return suggestedType
}

export function formatBoardDraftSourcePostDate(isoTimestamp: string | null): string | null {
  if (!isoTimestamp) return null
  const parsedDate = new Date(isoTimestamp)
  if (Number.isNaN(parsedDate.getTime())) return null
  return parsedDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}
